import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Loading from '../../../../components/Loading/Loading';
import Message from '../../../../components/Message/Message';
import Notification from '../../../../components/Notification/Notification';
import useNotification from '../../../../hooks/useNotification';
import PlayerGuessesModal from '../PlayerGuessesModal/PlayerGuessesModal';
import './Leaderboard.css';

const Leaderboard = ({ onRefresh }) => {
  const { showNotification, notificationProps } = useNotification();
  // State til at holde styr på valgt spiller og modal
  const [selectedPlayer, setSelectedPlayer] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Hent data fra Redux state (data er allerede preloaded via usePreloadLeaderboardData)
  const allGameData = useSelector((state) => state.game.allGameData);
  const allGameDataLoading = useSelector((state) => state.game.allGameDataLoading);
  const allGameDataError = useSelector((state) => state.game.allGameDataError);
  useEffect(() => {
    if (allGameDataError) {
      showNotification(`Fejl ved hentning af leaderboard data: ${allGameDataError}`, 'error');
    }
  }, [allGameDataError, showNotification]);

  const phaseLocks = useSelector((state) => state.game.phaseLocks);
  const settingsLoading = useSelector((state) => state.game.settingsLoading);
  const settingsData = useSelector((state) => state.game.settingsData);
  const realResults = settingsData?.realResults || {};
  // Kombiner spil data med bruger information og filtrer kun brugere der har gættet
  const leaderboardData = useMemo(() => {
    if (!allGameData || allGameData.length === 0) {
      return [];
    }

    // Filtrer kun brugere der har gættet og som ikke er slettet
    const playersWithGuesses = allGameData
      .filter(game => game.mateName && game.mateName !== 'Ukendt bruger') // Filtrer slettede brugere
      .map(game => {
        const hasGuessed = game.preseasonWinner || 
                          (game.leagueStanding && game.leagueStanding.length > 0) ||
                          (game.playoffPredictions && game.playoffPredictions !== '{}') ||
                          (game.knockoutPredictions && game.knockoutPredictions !== '{}') ||
                          game.finalPrediction;

        return {
          id: game.id,
          mateId: game.mateId,
          name: game.mateName,
          points: game.points || 0,
          hasGuessed
        };
      })
      .filter(player => player.hasGuessed) // Kun brugere der har gættet
      .sort((a, b) => b.points - a.points) // Sorter efter point (højeste først)
      .map((player, index) => ({
        ...player,
        rank: index + 1
      }));

    return playersWithGuesses;
  }, [allGameData]);

  const getRankIcon = (rank, totalPlayers) => {
    // Hvis spilleren er på sidstepladsen
    if (rank === totalPlayers && totalPlayers > 1) {
      return '💩';
    }
    
    switch (rank) {
      case 1:
        return '🥇';
      case 2:
        return '🥈';
      case 3:
        return '🥉';
      default:
        return `${rank}`;
    }
  };

  const getRankClass = (rank, totalPlayers) => {
    if (rank === 1) return 'first-place';
    if (rank === 2) return 'second-place';
    if (rank === 3) return 'third-place';
    if (rank === totalPlayers && totalPlayers > 1) return 'last-place';
    return 'regular';
  };

  // Håndter klik på spiller
  const handlePlayerClick = (player) => {
    // Find den fulde spiller data fra Redux state
    const fullPlayerData = allGameData.find(
      game => game.mateId === player.mateId
    );
    
    if (fullPlayerData) {
      setSelectedPlayer({ ...player, ...fullPlayerData });
      setIsModalOpen(true);
    }
  };

  // Luk modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
      setSelectedPlayer({});
  };

  const handleRefresh = async () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Loading state
  if (allGameDataLoading || settingsLoading) {
    return (
      <div className="leaderboard-container">
        <div className="leaderboard-header">
          <div className="header-top">
            <h1>🏆 Leaderboard</h1>
          </div>
          <p>Se hvem der fører i Champions League spillet</p>
        </div>
        <Loading text="Henter leaderboard data..." />
      </div>
    );
  }

  // Error state
  if (allGameDataError) {
    return (
      <div className="leaderboard-container">
        <Notification {...notificationProps} />
        <div className="leaderboard-header">
          <div className="header-top">
            <h1>🏆 Leaderboard</h1>
            {onRefresh && (
              <button
                type="button"
                className="leaderboard-refresh-button"
                onClick={handleRefresh}
                disabled={isRefreshing}
                title="Prøv at hente data igen"
              >
                {isRefreshing ? 'Opdaterer…' : 'Opdater'}
              </button>
            )}
          </div>
          <p>Se hvem der fører i Champions League spillet</p>
        </div>
        <p className="leaderboard-error-text">Kunne ikke hente leaderboard-data. Prøv at opdatere.</p>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <Notification {...notificationProps} />
      <div className="leaderboard-header">
        <div className="header-top">
          <h1>🏆 Leaderboard</h1>
          {onRefresh && (
            <button
              type="button"
              className="leaderboard-refresh-button"
              onClick={handleRefresh}
              title="Hent seneste point og stilling"
            >
              {isRefreshing ? 
                <Loading size="small" variant="refresh" inline text="" /> 
                : 
                <span className="leaderboard-refresh-button-icon">
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path
                      d="M21 12a9 9 0 1 1-3.3-6.9"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <polyline
                      points="21 3 21 9 15 9"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              }
            </button>
          )}
        </div>
        <p>Se stillingen i Champions League spillet</p>
        {leaderboardData.length > 0 && (
          <p className="player-count">
            {leaderboardData.length} spiller{leaderboardData.length !== 1 ? 'e' : ''} har gættet
          </p>
        )}
      </div>
      
      {leaderboardData.length === 0 ? (
        <Message type="info">
          Ingen brugere har gættet i Champions League spillet endnu.
        </Message>
      ) : (
        <div className="leaderboard-table-container">
          <table className="leaderboard-table">
            <thead>
              <tr>
                <th className="rank-header">Placering</th>
                <th className="name-header">Navn</th>
                <th className="points-header">Point</th>
              </tr>
            </thead>
            <tbody>
              {leaderboardData.map((player) => (
                <tr 
                  key={player.id} 
                  className={`leaderboard-row ${getRankClass(player.rank, leaderboardData.length)} clickable`}
                  onClick={() => handlePlayerClick(player)}
                  title="Klik for at se spillerens gæt"
                >
                  <td className="rank-cell">
                    <span className="rank-icon">{getRankIcon(player.rank, leaderboardData.length)}</span>
                  </td>
                  <td className="name-cell">
                    <span className="player-name">{player.name}</span>
                  </td>
                  <td className="points-cell">
                    <span className="points-value">{player.points}</span>
                    <span className="points-label">point</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal til at vise spillerens gæt */}
      <PlayerGuessesModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        player={selectedPlayer}
        phaseLocks={phaseLocks}
        realResults={realResults}
      />
    </div>
  );
};

export default Leaderboard;
